/**
 * QuoteForm Component
 *
 * Local fallback form component for Request Quote when the HubSpot V4
 * embed cannot be used (e.g., script blocked by ad blocker / network).
 */
import React, { useState, ReactNode, useImperativeHandle } from 'react';
import { Box, Select, MenuItem, FormControl, styled } from '@mui/material';
import { useSelector } from 'react-redux';
import { getCurrentValue } from 'reduxModules/selectors/containerSelectors';
import { useToast } from '../../contexts/ToastContext';
import Input from './Input';
import InputLabel from './InputLabel';

export interface QuoteFormData {
  designName: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  company: string;
  city: string;
  country: string;
}

export interface QuoteFormHandle {
  getFormData: () => QuoteFormData;
  validate: () => boolean;
}

interface QuoteFormProps {
  onSubmit?: (data: QuoteFormData) => void;
  formRef?: React.RefObject<QuoteFormHandle | null>;
}

const QuoteForm: React.FC<QuoteFormProps> = ({ onSubmit, formRef }) => {
  const savedDesignName = useSelector((state: any) => getCurrentValue(state, 'name')) || '';
  const { showError } = useToast();

  const [formData, setFormData] = useState({
    designName: savedDesignName || 'Conference room Md',
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    company: '',
    city: '',
    country: ''
  });

  const [fieldErrors, setFieldErrors] = useState({
    firstName: '',
    lastName: '',
    email: ''
  });

  const handleChange = (field: string) => (event: any) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
    // Clear error for this field when user starts typing
    if (fieldErrors[field as keyof typeof fieldErrors]) {
      setFieldErrors({
        ...fieldErrors,
        [field]: ''
      });
    }
  };

  // Expose form methods via ref
  React.useImperativeHandle(formRef, () => ({
    getFormData: () => formData,
    validate: () => {
      // Reset errors
      const newErrors = {
        firstName: '',
        lastName: '',
        email: ''
      };
      let hasErrors = false;

      // Validate required fields
      if (!formData.firstName?.trim()) {
        newErrors.firstName = 'First name is required';
        hasErrors = true;
      }

      if (!formData.lastName?.trim()) {
        newErrors.lastName = 'Last name is required';
        hasErrors = true;
      }

      if (!formData.email?.trim()) {
        newErrors.email = 'Email is required';
        hasErrors = true;
      } else {
        // Email format validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email.trim())) {
          newErrors.email = 'Please enter a valid email address';
          hasErrors = true;
        }
      }

      // Update field errors for inline display
      setFieldErrors(newErrors);

      // Show toast with error summary if there are errors
      if (hasErrors) {
        const errorCount = Object.values(newErrors).filter(e => e).length;
        showError(`Please fix ${errorCount} error${errorCount > 1 ? 's' : ''} in the form`);
        return false;
      }

      return true;
    }
  }), [formData]);

  const countries = [
    'United States',
    'Canada',
    'United Kingdom',
    'Germany',
    'France',
    'Australia',
    'Japan',
    'China',
    'India',
    'Brazil',
    'Mexico',
    'Other'
  ];

  return (
    <StyledFormContainer>
      <FormGroup>
        <InputLabel>Design name</InputLabel>
        <Input
          value={formData.designName}
          onChange={handleChange('designName')}
          fullWidth
          size="small"
        />
      </FormGroup>

      <TwoColumnGroup>
        <FormGroup>
          <InputLabel required>First name</InputLabel>
          <Input
            placeholder="ex. Jane"
            value={formData.firstName}
            onChange={handleChange('firstName')}
            fullWidth
            size="small"
            required
            error={!!fieldErrors.firstName}
            helperText={fieldErrors.firstName}
          />
        </FormGroup>
        <FormGroup>
          <InputLabel required>Last name</InputLabel>
          <Input
            placeholder="ex. Doe"
            value={formData.lastName}
            onChange={handleChange('lastName')}
            fullWidth
            size="small"
            required
            error={!!fieldErrors.lastName}
            helperText={fieldErrors.lastName}
          />
        </FormGroup>
      </TwoColumnGroup>

      <TwoColumnGroup>
        <FormGroup>
          <InputLabel required>Email</InputLabel>
          <Input
            placeholder="name@example.com"
            value={formData.email}
            onChange={handleChange('email')}
            fullWidth
            size="small"
            type="email"
            required
            error={!!fieldErrors.email}
            helperText={fieldErrors.email}
          />
        </FormGroup>
        <FormGroup>
          <InputLabel>Phone number</InputLabel>
          <Input
            placeholder="Phone number"
            value={formData.phoneNumber}
            onChange={handleChange('phoneNumber')}
            fullWidth
            size="small"
            type="tel"
          />
        </FormGroup>
      </TwoColumnGroup>

      <TwoColumnGroup>
        <FormGroup>
          <InputLabel>Company</InputLabel>
          <Input
            placeholder="Company name"
            value={formData.company}
            onChange={handleChange('company')}
            fullWidth
            size="small"
          />
        </FormGroup>
        <FormGroup>
          <InputLabel>City</InputLabel>
          <Input
            placeholder="City"
            value={formData.city}
            onChange={handleChange('city')}
            fullWidth
            size="small"
          />
        </FormGroup>
      </TwoColumnGroup>

      <FormGroup>
        <InputLabel>Country</InputLabel>
        <Select
          value={formData.country}
          onChange={handleChange('country')}
          displayEmpty
          fullWidth
          size="small"
          variant="outlined"
          renderValue={(selected: any) => {
            if (!selected) {
              return <SelectPlaceholder>Select a country</SelectPlaceholder>;
            }
            return selected as React.ReactNode;
          }}
          sx={{
            backgroundColor: theme => theme.palette.background.paper,
            borderRadius: '4px',
            boxShadow: '0 1px 1px 0 rgba(0, 0, 0, 0.05)',
            fontSize: '12px',
            fontFamily: '"Open Sans"',
            fontWeight: 400,
            '& .MuiOutlinedInput-notchedOutline': {
              borderWidth: '0.6px',
              borderStyle: 'solid',
              borderColor: theme => theme.palette.mode === 'light'
                ? 'rgba(17, 17, 17, 0.15)'
                : theme.palette.divider,
              borderRadius: '4px',
              transition: 'border-color 0.2s',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme => theme.palette.mode === 'light'
                ? 'rgba(17, 17, 17, 0.25)'
                : theme.palette.action.hover,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderWidth: '1px',
              borderColor: theme => theme.palette.primary.main,
            },
            '& .MuiSelect-select': {
              padding: '8px 12px',
              fontSize: '12px',
              fontWeight: 400,
              color: theme => theme.palette.text.primary,
            },
          }}
        >
          <MenuItem value="">
            <SelectPlaceholder>Select a country</SelectPlaceholder>
          </MenuItem>
          {countries.map((country) => (
            <MenuItem key={country} value={country}>
              {country}
            </MenuItem>
          ))}
        </Select>
      </FormGroup>
    </StyledFormContainer>
  );
};

const StyledFormContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  padding: '0px',
  backgroundColor: theme.palette.background.paper,
}));

const FormGroup = styled(Box)(({ theme }) => ({
  marginBottom: '10px',
  width: '100%',
}));

const TwoColumnGroup = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '16px',
  marginBottom: '10px',
}));

const SelectPlaceholder = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

export default QuoteForm;